import { AlkLogger } from '@alk/core';
import { COMMUNICATION_SERVICE, ICommunicationService } from '../../communication';
import { decodeDisclaimerApprovalSaveResponse, decodeDisclaimerResponse } from './disclaimer-adapter.service.utils';
import { DisclaimerApprovalSaveRequest, DisclaimerRequest, MessageTypes } from 'sfx-web-contract';
import { filter, finalize, map, take, takeUntil, tap } from 'rxjs/operators';
import { IDisclaimerAdapterService } from './disclaimer-adapter.interface';
import { IDisclaimerApprovalSaveResponse, IDisclaimerResponse } from '../../../interfaces/disclaimer-message.interface';
import { inject, injectable } from 'inversify';
import { ISessionLifecycle, SESSION_LIFECYCLE_SERVICE } from '../../session-lifecycle';

@injectable()
export class DisclaimerAdapterService implements IDisclaimerAdapterService {
    @inject(COMMUNICATION_SERVICE) private readonly communicationService: ICommunicationService;
    @inject(SESSION_LIFECYCLE_SERVICE) private readonly sessionLifeCycle: ISessionLifecycle;

    getDisclaimerInfo(): Promise<IDisclaimerResponse> {
        const message = DisclaimerRequest.encode(
            DisclaimerRequest.create({}),
        ).finish();

        const requestId = this.communicationService.create(message, MessageTypes.Disclaimer);

        return this.communicationService.dataObservable(MessageTypes.Disclaimer).pipe(
            filter(response => response.communicationToken === requestId),
            map(decodeDisclaimerResponse),
            tap((response) => AlkLogger.info([
                '[DisclaimerAdapterService#getDisclaimerInfo]',
                JSON.stringify(response),
            ].join(' '))),
            finalize(() => {
                AlkLogger.debug(`[DisclaimerAdapterService#getDisclaimerInfo] feed complete for ${ requestId }`);
                this.communicationService.delete(requestId);
            }),
            take(1),
            takeUntil(this.sessionLifeCycle.destroyObservable()),
        ).toPromise();
    }

    sendApproval(): Promise<IDisclaimerApprovalSaveResponse> {
        const message = DisclaimerApprovalSaveRequest.encode(
            DisclaimerApprovalSaveRequest.create({}),
        ).finish();

        const requestId = this.communicationService.create(message, MessageTypes.DisclaimerApprovalSave);

        return this.communicationService.dataObservable(MessageTypes.DisclaimerApprovalSave).pipe(
            filter(response => response.communicationToken === requestId),
            map(decodeDisclaimerApprovalSaveResponse),
            tap((response) => AlkLogger.info([
                '[DisclaimerAdapterService#sendApproval]',
                JSON.stringify(response),
            ].join(' '))),
            finalize(() => {
                AlkLogger.debug(`[DisclaimerAdapterService#sendApproval] feed complete for ${ requestId }`);
                this.communicationService.delete(requestId);
            }),
            take(1),
            takeUntil(this.sessionLifeCycle.destroyObservable()),
        ).toPromise();
    }
}
